import React, { Dispatch, SetStateAction } from "react";
import {
  Alert,
  Box,
  Card,
  Heading,
  Stack,
  Strong,
  Text,
} from "@origin-digital/ods-core";
import { TileButton, TileButtonGroup } from "@origin-digital/ods-tile-button";
import offersContent from "../OfferCard/content.json";
import content from "./content.json";
import { APP_NAME } from "../../consts";
import { submitInteractionEvent } from "../../utils/analytics";
import { OfferTermsSection } from "../OfferTermsSection/OfferTermsSection";
import { ILPGOffer } from "../../model/ILPGOffer";
import { PreAddressCards } from "../PreAddressCards/PreAddressCards";
import { PageLoader } from "../PageLoader/PageLoader";
import { OfferSectionInfoBanner } from "../OfferSectionInfoBanner/OfferSectionInfoBanner";
import { MeteredLPGBlocking } from "../MeteredLPGBlocking/MeteredLPGBlocking";
import { LocationType } from "../../model/ILocationValidation";
import { OfferType, IOfferTab, IOfferTabAnalytics } from "../Offers/offerTab";

export const OfferSection = ({
  type,
  onChangeType,
  offerSelector,
  offerDetailCard,
  offerTypeUnavailable,
  unableToSignup,
  authDealerScreen,
  hasOffers,
  loading,
  hasNaturalGas,
  naturalGasOriginAddressId,
  locationType,
  hideBottleTypeSection,
  selectedOffer,
  offerTab,
  analyticsSent,
  setAnalyticsSent,
}: {
  type: OfferType;
  onChangeType: Dispatch<SetStateAction<OfferType>>;
  offerSelector?: React.ReactNode;
  offerDetailCard?: React.ReactNode;
  offerTypeUnavailable?: React.ReactNode;
  unableToSignup?: React.ReactNode;
  authDealerScreen?: React.ReactNode;
  hasOffers?: boolean;
  loading?: boolean;
  hasNaturalGas?: boolean;
  naturalGasOriginAddressId?: string;
  locationType?: LocationType;
  hideBottleTypeSection?: boolean;
  selectedOffer?: ILPGOffer;
  offerTab?: IOfferTab;
  analyticsSent?: IOfferTabAnalytics;
  setAnalyticsSent?: (key: IOfferTabAnalytics) => void;
}) => {
  const isMetered = locationType === LocationType.METERED;
  const showIncorrectProductTypeWarning =
    type === OfferType.REFILL && locationType === LocationType.EXCHANGE;

  const showInternetTerms =
    !loading &&
    !!offerDetailCard &&
    !unableToSignup &&
    !authDealerScreen &&
    !offerTypeUnavailable;

  const handleChange = (newValue: string) => {
    submitInteractionEvent(
      "click",
      "button",
      "energy-plans",
      APP_NAME,
      newValue === OfferType.REFILL
        ? offersContent.refill.tab
        : offersContent.exchange.tab
    );
    onChangeType(newValue as OfferType);
  };

  return (
    <>
      {!hasOffers && loading && <PageLoader />}
      {!hasOffers &&
        !loading &&
        (unableToSignup ?? offerTypeUnavailable ?? <PreAddressCards />)}
      {hasOffers && isMetered && (
        <MeteredLPGBlocking
          offerTab={offerTab || ({} as IOfferTab)}
          analyticsSent={analyticsSent}
          setAnalyticsSent={setAnalyticsSent || (() => {})}
          shouldFireAlert={true}
        />
      )}
      {hasOffers && !isMetered && (
        <Card
          paddingXSpecific={{ xs: "none", lg: "xxlarge" }}
          paddingYSpecific={{ xs: "large", sm: "xxlarge" }}
          blur="small"
          borderRadius="large"
          data-id="offers-card"
        >
          <Stack space="xxlarge">
            {!hideBottleTypeSection && (
              <Box paddingX={{ xs: "medium", sm: "xxlarge", lg: "none" }}>
                <Stack space="medium" data-id="bottle-type-section">
                  <Heading
                    component="h2"
                    variant="h3"
                    weight="medium"
                    data-id="bottle-type-heading"
                  >
                    {content.bottleTypeSection.heading}
                  </Heading>
                  {hasNaturalGas && (
                    <OfferSectionInfoBanner
                      title={content.naturalGasBanner.title}
                      body={content.naturalGasBanner.body}
                      linkText={content.naturalGasBanner.linkText}
                      linkHref={`/electricity-gas/plans.html?originAddressId=${naturalGasOriginAddressId}`}
                    />
                  )}
                  <TileButtonGroup
                    id="bottle-type-selector"
                    data-id="bottle-type-selector"
                    value={type}
                    cols={2}
                    togglePosition="right"
                    hideHelperTextSpace
                    noChildPadding
                    aria-label="LPG type"
                    onChange={(_event, val) => {
                      if (val) handleChange(val as string);
                    }}
                  >
                    <TileButton
                      aria-label={content.exchange.ariaLabel}
                      data-id="offer-filter-button-exchange"
                      id="exchange-tile"
                      value={OfferType.EXCHANGE}
                      selected={type === OfferType.EXCHANGE}
                      responsiveLayout
                      multiLineDesc={true}
                      backgroundColor="secondary"
                      label={
                        <Stack space="xsmall">
                          <Box display="flex">
                            <img
                              src={content.exchange.imageUrl}
                              width={72}
                              height={72}
                              alt=""
                            />
                          </Box>
                        </Stack>
                      }
                    >
                      <Stack space="xsmall">
                        <Text
                          variant="subtitle"
                          weight="medium"
                          tone="neutralDark"
                        >
                          {offersContent.exchange.tab}
                        </Text>
                        <Text>{content.exchange.description}</Text>
                      </Stack>
                    </TileButton>
                    <TileButton
                      aria-label={content.refill.ariaLabel}
                      data-id="offer-filter-button-refill"
                      id="refill-tile"
                      value={OfferType.REFILL}
                      selected={type === OfferType.REFILL}
                      responsiveLayout
                      multiLineDesc={true}
                      backgroundColor="secondary"
                      label={
                        <Box display="flex">
                          <img
                            src={content.refill.imageUrl}
                            width={72}
                            height={72}
                            alt=""
                          />
                        </Box>
                      }
                    >
                      <Stack space="xsmall">
                        <Text
                          variant="subtitle"
                          weight="medium"
                          tone="neutralDark"
                        >
                          {offersContent.refill.tab}
                        </Text>
                        <Text>{content.refill.description}</Text>
                      </Stack>
                    </TileButton>
                  </TileButtonGroup>
                  {showIncorrectProductTypeWarning && (
                    <Alert
                      data-id="incorrect-product-type-warning"
                      tone="caution"
                      title={content.incorrectProductTypeWarning.title}
                    >
                      {content.incorrectProductTypeWarning.prefix}{" "}
                      <Strong>
                        {content.incorrectProductTypeWarning.productType}
                      </Strong>{" "}
                      {content.incorrectProductTypeWarning.suffix}
                    </Alert>
                  )}
                </Stack>
              </Box>
            )}
            {unableToSignup ?? authDealerScreen ?? offerTypeUnavailable ?? (
              <Stack>
                {offerSelector}
                {offerDetailCard && (
                  <Box paddingX={{ xs: "medium", sm: "xxlarge", lg: "none" }}>
                    {offerDetailCard}
                  </Box>
                )}
              </Stack>
            )}
            <Box paddingX={{ xs: "medium", sm: "xxlarge", lg: "none" }}>
              <Stack space="small">
                <OfferTermsSection
                  offerType={type}
                  selectedOffer={selectedOffer}
                />
                {showInternetTerms && (
                  <Text
                    variant="body-small"
                    tone="neutral"
                    data-id="internet-offer-terms"
                  >
                    {content.internetTerms}
                  </Text>
                )}
              </Stack>
            </Box>
          </Stack>
        </Card>
      )}
    </>
  );
};
